/**
 * Flatten USP response into a simple path → value map.
 * Uses full path (resolved.path + param.path) as key.
 */
export const flattenUspResponse = (data) => {
  if (!data?.results) return {};
  const flat = {};
  for (const item of data.results) {
    if (item?.resolved_paths) {
      for (const resolved of item.resolved_paths) {
        if (resolved.parameters) {
          for (const param of resolved.parameters) {
            flat[`${resolved.path}${param.path}`] = param.value;
          }
        }
      }
    }
  }
  return flat;
};

/**
 * Flatten USP response using only param.path as key (no resolved.path prefix).
 * Useful for single-object responses like Device.DeviceInfo.
 */
export const flattenUspParams = (data) => {
  if (!data?.results) return {};
  const result = {};
  for (const item of data.results) {
    if (item?.resolved_paths) {
      for (const resolved of item.resolved_paths) {
        if (resolved.parameters) {
          for (const param of resolved.parameters) {
            result[param.path] = param.value;
          }
        }
      }
    }
  }
  return result;
};

// TR-181 object references are returned inconsistently by agents: some include
// the trailing dot ('Device.IP.Interface.2.'), some don't, and list-typed
// parameters (LowerLayers, Interface on some objects) are comma-separated. Raw
// string equality therefore silently fails to match — compare normalised refs.
const normalizeRef = (ref) =>
  String(ref ?? '')
    .trim()
    .replace(/\.$/, '');
const refList = (value) =>
  String(value ?? '')
    .split(',')
    .map(normalizeRef)
    .filter(Boolean);
const refMatches = (value, target) => refList(value).includes(normalizeRef(target));

/**
 * Parse flat USP data into categorized network interface objects.
 * Returns { ethInterfaces, ethLinks, ipInterfaces, pppInterfaces, dhcpClients,
 *           ipv4Forwardings, dnsServers }
 */
export const parseNetworkInterfaces = (flat) => {
  const ethInterfaces = {};
  const ethLinks = {};
  const ipInterfaces = {};
  const pppInterfaces = {};
  const dhcpClients = {};
  const ipv4Forwardings = {};
  const dnsServers = {};

  for (const [key, value] of Object.entries(flat)) {
    const ethMatch = key.match(/^Device\.Ethernet\.Interface\.(\d+)\.(.+)$/);
    if (ethMatch) {
      const idx = ethMatch[1];
      if (!ethInterfaces[idx]) ethInterfaces[idx] = {};
      ethInterfaces[idx][ethMatch[2]] = value;
      continue;
    }
    const linkMatch = key.match(/^Device\.Ethernet\.Link\.(\d+)\.(.+)$/);
    if (linkMatch) {
      const idx = linkMatch[1];
      if (!ethLinks[idx]) ethLinks[idx] = {};
      ethLinks[idx][linkMatch[2]] = value;
      continue;
    }
    const ipMatch = key.match(/^Device\.IP\.Interface\.(\d+)\.(.+)$/);
    if (ipMatch) {
      const idx = ipMatch[1];
      if (!ipInterfaces[idx]) ipInterfaces[idx] = {};
      ipInterfaces[idx][ipMatch[2]] = value;
      continue;
    }
    const pppMatch = key.match(/^Device\.PPP\.Interface\.(\d+)\.(.+)$/);
    if (pppMatch) {
      const idx = pppMatch[1];
      if (!pppInterfaces[idx]) pppInterfaces[idx] = {};
      pppInterfaces[idx][pppMatch[2]] = value;
      continue;
    }
    const dhcpMatch = key.match(/^Device\.DHCPv4\.Client\.(\d+)\.(.+)$/);
    if (dhcpMatch) {
      const idx = dhcpMatch[1];
      if (!dhcpClients[idx]) dhcpClients[idx] = {};
      dhcpClients[idx][dhcpMatch[2]] = value;
      continue;
    }
    // Routing table — the default route is the fallback source for the WAN
    // gateway when no DHCP client reports IPRouters (static WAN, or an agent
    // that doesn't populate Device.DHCPv4.Client).
    const routeMatch = key.match(/^Device\.Routing\.Router\.(\d+)\.IPv4Forwarding\.(\d+)\.(.+)$/);
    if (routeMatch) {
      const id = `${routeMatch[1]}.${routeMatch[2]}`;
      if (!ipv4Forwardings[id]) ipv4Forwardings[id] = {};
      ipv4Forwardings[id][routeMatch[3]] = value;
      continue;
    }
    // Resolver list — fallback source for WAN DNS.
    const dnsMatch = key.match(/^Device\.DNS\.Client\.Server\.(\d+)\.(.+)$/);
    if (dnsMatch) {
      const idx = dnsMatch[1];
      if (!dnsServers[idx]) dnsServers[idx] = {};
      dnsServers[idx][dnsMatch[2]] = value;
    }
  }

  return { ethInterfaces, ethLinks, ipInterfaces, pppInterfaces, dhcpClients, ipv4Forwardings, dnsServers };
};

// Default route (0.0.0.0/0) entries carry the gateway; some agents leave
// Dest*/mask empty instead of writing 0.0.0.0.
const isDefaultRoute = (route) => {
  const isAny = (v) => v === undefined || v === '' || v === '0.0.0.0';
  return isAny(route.DestIPAddress) && isAny(route.DestSubnetMask);
};

// Gateway from the routing table: prefer a default route bound to the WAN IP
// interface, otherwise any enabled default route that has a gateway.
const findDefaultRouteGateway = (ipv4Forwardings, wanIpIdx) => {
  const candidates = Object.values(ipv4Forwardings).filter(
    (r) => isDefaultRoute(r) && r.GatewayIPAddress && r.Enable !== '0' && r.Status !== 'Disabled'
  );
  const onWan = candidates.find((r) => refMatches(r.Interface, `Device.IP.Interface.${wanIpIdx}`));
  return (onWan || candidates[0])?.GatewayIPAddress || null;
};

// DNS from Device.DNS.Client.Server: prefer servers bound to the WAN interface
// (entries without an Interface reference count as unbound and are kept).
const findClientDnsServers = (dnsServers, wanIpIdx) => {
  const enabled = Object.values(dnsServers).filter((s) => s.DNSServer && s.Enable !== '0' && s.Status !== 'Disabled');
  const onWan = enabled.filter((s) => !s.Interface || refMatches(s.Interface, `Device.IP.Interface.${wanIpIdx}`));
  const list = [...new Set((onWan.length ? onWan : enabled).map((s) => s.DNSServer))];
  return list.length ? list.join(', ') : null;
};

export const resolveInterfaces = (flat) => {
  const { ethInterfaces, ethLinks, ipInterfaces, pppInterfaces, dhcpClients, ipv4Forwardings, dnsServers } = parseNetworkInterfaces(flat);

  // Alias-based detection (preferred)
  let wanEthIdx = Object.entries(ethInterfaces).find(([, v]) => v.Alias === 'eth-wan')?.[0];
  let wanIpIdx = Object.entries(ipInterfaces).find(([, v]) => v.Alias === 'cpe-wan')?.[0];
  let lanIpIdx = Object.entries(ipInterfaces).find(([, v]) => v.Alias === 'cpe-lan')?.[0];

  // Fallback: Upstream + LowerLayers chain
  if (!wanIpIdx) {
    if (!wanEthIdx) {
      wanEthIdx = Object.entries(ethInterfaces).find(([, v]) => v.Upstream === '1')?.[0];
    }
    const wanLinkIdx = Object.entries(ethLinks).find(([, v]) => refMatches(v.LowerLayers, `Device.Ethernet.Interface.${wanEthIdx}`))?.[0];
    wanIpIdx = Object.entries(ipInterfaces).find(([, v]) => refMatches(v.LowerLayers, `Device.Ethernet.Link.${wanLinkIdx}`))?.[0];
  }
  if (!lanIpIdx) {
    lanIpIdx = Object.entries(ipInterfaces).find(([idx, v]) => {
      if (idx === wanIpIdx) return false;
      const linkIdx = Object.entries(ethLinks).find(([li]) => refMatches(v.LowerLayers, `Device.Ethernet.Link.${li}`))?.[0];
      if (!linkIdx) return false;
      const ll = ethLinks[linkIdx]?.LowerLayers || '';
      return ll.startsWith('Device.Bridging.') || ll.includes('Ethernet.Interface');
    })?.[0];
  }

  // PPPoE is only in use when a WAN IP interface is actually layered on an enabled
  // PPP interface. An enabled/"Connected" PPP object that no IP interface
  // references is stale config and is ignored (otherwise DHCP is misreported as
  // PPPoE). ConnectionStatus is intentionally NOT checked: a configured PPPoE WAN
  // that is momentarily disconnected is still PPPoE, and the IP interface Status
  // reflects the live state. Every enabled PPP interface is scanned (not just the
  // first) so a stale PPP.1 does not mask a real PPP.2 an IP interface layers on.
  let pppEntry = null;
  let pppWanIpIdx;
  for (const entry of Object.entries(pppInterfaces)) {
    if (entry[1].Enable !== '1') continue;
    const ipIdx = Object.entries(ipInterfaces).find(([, v]) => refMatches(v.LowerLayers, `Device.PPP.Interface.${entry[0]}`))?.[0];
    if (ipIdx) {
      pppEntry = entry;
      pppWanIpIdx = ipIdx;
      break;
    }
  }
  const isPppoe = Boolean(pppWanIpIdx);

  // PPPoE overrides wanIpIdx with the PPP-backed IP interface
  if (isPppoe) wanIpIdx = pppWanIpIdx;

  let wanIp = '-';
  let wanSubnet = '-';
  let wanAddressingType = '-';
  let wanDns = '-';
  let wanGateway = '-';

  // Gateway/DNS are reported in different places depending on WAN type and how
  // complete the agent's data model is, so each has a fallback chain:
  //   PPPoE  -> IPCP.RemoteIPAddress / IPCP.DNSServers
  //   DHCP   -> DHCPv4.Client.{i}.IPRouters / .DNSServers
  //   either -> default route in Device.Routing / Device.DNS.Client.Server
  // The last step also covers a static WAN, where neither PPP nor a DHCP client
  // exists at all.
  const routeGateway = wanIpIdx ? findDefaultRouteGateway(ipv4Forwardings, wanIpIdx) : null;
  const clientDns = wanIpIdx ? findClientDnsServers(dnsServers, wanIpIdx) : null;

  if (isPppoe && pppEntry) {
    const ppp = pppEntry[1];
    wanIp = ppp['IPCP.LocalIPAddress'] || ipInterfaces[wanIpIdx]?.['IPv4Address.1.IPAddress'] || '-';
    wanGateway = ppp['IPCP.RemoteIPAddress'] || routeGateway || '-';
    wanDns = ppp['IPCP.DNSServers'] || clientDns || '-';
    wanAddressingType = 'PPPoE';
  } else if (wanIpIdx) {
    wanIp = ipInterfaces[wanIpIdx]?.['IPv4Address.1.IPAddress'] || '-';
    wanSubnet = ipInterfaces[wanIpIdx]?.['IPv4Address.1.SubnetMask'] || '-';
    wanAddressingType = ipInterfaces[wanIpIdx]?.['IPv4Address.1.AddressingType'] || '-';
    const dhcpEntry = Object.entries(dhcpClients).find(([, v]) => refMatches(v.Interface, `Device.IP.Interface.${wanIpIdx}`));
    wanGateway = dhcpEntry?.[1].IPRouters || routeGateway || '-';
    wanDns = dhcpEntry?.[1].DNSServers || clientDns || '-';
  }

  let wanIpv6Address = '-';
  let wanIpv6Prefix = '-';
  if (wanIpIdx) {
    const wanIf = ipInterfaces[wanIpIdx];
    const ipv6Key = Object.keys(wanIf || {}).find((k) => k.match(/^IPv6Address\.\d+\.IPAddress$/));
    if (ipv6Key) {
      wanIpv6Address = wanIf[ipv6Key] || '-';
      const prefixKey = ipv6Key.replace('IPAddress', 'Prefix');
      wanIpv6Prefix = wanIf[prefixKey] || '-';
    }
  }

  const lanEthIdx =
    Object.entries(ethInterfaces).find(([, v]) => v.Alias === 'eth-lan1')?.[0] ||
    Object.entries(ethInterfaces).find(([, v]) => v.Upstream === '0')?.[0];

  const wan = wanIpIdx
    ? {
        status: ipInterfaces[wanIpIdx]?.Status || '-',
        ipAddress: wanIp,
        subnetMask: wanSubnet,
        addressingType: wanAddressingType,
        gateway: wanGateway,
        dns: wanDns,
        ipv6Address: wanIpv6Address,
        ipv6Prefix: wanIpv6Prefix,
        macAddress: wanEthIdx ? ethInterfaces[wanEthIdx]?.MACAddress || '-' : '-',
        mtu: ipInterfaces[wanIpIdx]?.MaxMTUSize || '-',
        pppoe: isPppoe ? { username: pppEntry[1].Username || '-', connectionStatus: pppEntry[1].ConnectionStatus || '-' } : null
      }
    : null;

  const lan = lanIpIdx
    ? {
        status: ipInterfaces[lanIpIdx]?.Status || '-',
        ipAddress: ipInterfaces[lanIpIdx]?.['IPv4Address.1.IPAddress'] || '-',
        subnetMask: ipInterfaces[lanIpIdx]?.['IPv4Address.1.SubnetMask'] || '-',
        macAddress: lanEthIdx ? ethInterfaces[lanEthIdx]?.MACAddress || '-' : '-',
        mtu: ipInterfaces[lanIpIdx]?.MaxMTUSize || '-'
      }
    : null;

  return { wan, lan };
};

/**
 * Split a USP full path into its ancestor chain of tree nodes.
 * Object paths end with '.', leaf (parameter) paths do not.
 * e.g. 'Device.IP.Interface.1.Status' (leaf) ->
 *   Device. (obj) > Device.IP. (obj) > Device.IP.Interface. (obj)
 *   > Device.IP.Interface.1. (obj) > Device.IP.Interface.1.Status (leaf)
 */
export const pathToNodeChain = (fullPath, isObject) => {
  if (!fullPath) return [];
  const trimmed = isObject ? fullPath.replace(/\.$/, '') : fullPath;
  if (!trimmed) return [];
  const segments = trimmed.split('.');
  const chain = [];
  let acc = '';
  segments.forEach((seg, i) => {
    acc += seg;
    const isLeaf = i === segments.length - 1 && !isObject;
    if (isLeaf) {
      chain.push({ path: acc, name: seg, isObject: false });
    } else {
      acc += '.';
      chain.push({ path: acc, name: seg, isObject: true });
    }
  });
  return chain;
};

/**
 * Convert a raw USP `get` response into a flat list of tree nodes
 * ({ path, name, isObject, value? }). Intermediate object nodes are derived
 * from path segmentation so the tree is complete regardless of how many levels
 * the response actually covers (depth-agnostic — safe to merge incrementally).
 */
export const uspResponseToNodes = (data) => {
  const map = {};
  const add = (node) => {
    const existing = map[node.path];
    if (existing) {
      if (!node.isObject && node.value !== undefined) existing.value = node.value;
    } else {
      map[node.path] = { ...node };
    }
  };

  if (data?.results) {
    for (const item of data.results) {
      if (!item?.resolved_paths) continue;
      for (const resolved of item.resolved_paths) {
        // The object itself (and its ancestors), even if it has no parameters
        // at this depth — this is what makes a branch expandable.
        if (resolved.path) {
          for (const node of pathToNodeChain(resolved.path, true)) add(node);
        }
        // Parameters. param.path may itself contain sub-object segments
        // (e.g. 'IPv4Address.1.IPAddress'), so segment the full path.
        if (resolved.parameters) {
          for (const param of resolved.parameters) {
            const chain = pathToNodeChain(`${resolved.path}${param.path}`, false);
            chain.forEach((node, i) => {
              if (i === chain.length - 1) add({ ...node, value: param.value });
              else add(node);
            });
          }
        }
      }
    }
  }
  return Object.values(map);
};

export const parseUspInfo = (flat) => {
  const agent = {};
  const controllers = {};
  const mtps = {};
  const mqttClients = {};
  const agentCertificates = {};

  for (const [key, value] of Object.entries(flat)) {
    const agentMatch = key.match(/^Device\.LocalAgent\.([A-Za-z0-9]+)$/);
    if (agentMatch) {
      agent[agentMatch[1]] = value;
      continue;
    }
    const agentCertMatch = key.match(/^Device\.LocalAgent\.Certificate\.(\d+)\.(.+)$/);
    if (agentCertMatch) {
      const idx = agentCertMatch[1];
      if (!agentCertificates[idx]) agentCertificates[idx] = {};
      agentCertificates[idx][agentCertMatch[2]] = value;
      continue;
    }
    const ctrlMatch = key.match(/^Device\.LocalAgent\.Controller\.(\d+)\.(.+)$/);
    if (ctrlMatch) {
      const idx = ctrlMatch[1];
      if (!controllers[idx]) controllers[idx] = {};
      controllers[idx][ctrlMatch[2]] = value;
      continue;
    }
    const mtpMatch = key.match(/^Device\.LocalAgent\.MTP\.(\d+)\.(.+)$/);
    if (mtpMatch) {
      const idx = mtpMatch[1];
      if (!mtps[idx]) mtps[idx] = {};
      mtps[idx][mtpMatch[2]] = value;
      continue;
    }
    const mqttMatch = key.match(/^Device\.MQTT\.Client\.(\d+)\.(.+)$/);
    if (mqttMatch) {
      const idx = mqttMatch[1];
      if (!mqttClients[idx]) mqttClients[idx] = {};
      mqttClients[idx][mqttMatch[2]] = value;
    }
  }

  return { agent, controllers, mtps, mqttClients, agentCertificates };
};
